
// Export all components from the data model
export * from './types';
export * from './mappers';
export * from './allocation-rules';
export * from './attribution-logic';
export * from './data-quality';

import { LeaderboardItem } from "../types";
import { 
  Account, 
  Product, 
  Geography, 
  SalesTransaction, 
  AllocationBasis,
  AttributionDimension
} from "./types";
import { 
  mapToAccounts, 
  mapToProducts, 
  mapToGeographies, 
  mapToSalesTransactions 
} from "./mappers";
import { allocationRules } from "./allocation-rules";
import { attributionRules, applyAttributionRules } from "./attribution-logic";
import { validateDataQuality, ValidationError } from "./data-quality";

/**
 * Comprehensive data model that encapsulates all business logic for
 * account-product-geo mapping, allocation rules, and attribution logic
 */
export class RevenueDataModel {
  private accounts: Account[] = [];
  private products: Product[] = [];
  private geographies: Geography[] = [];
  private transactions: SalesTransaction[] = [];
  private validationErrors: ValidationError[] = [];
  
  /**
   * Initialize the data model from leaderboard data
   */
  initialize(leaderboardData: LeaderboardItem[]) {
    this.accounts = mapToAccounts(leaderboardData);
    this.products = mapToProducts(leaderboardData);
    this.geographies = mapToGeographies(leaderboardData);
    this.transactions = mapToSalesTransactions(leaderboardData);
    
    // Validate data quality
    this.validationErrors = validateDataQuality(
      this.accounts, 
      this.products, 
      this.transactions
    );
    
    // Log any validation errors
    if (this.validationErrors.length > 0) {
      console.warn(`Found ${this.validationErrors.length} data quality issues:`);
      console.table(this.validationErrors);
    }
    
    return this;
  }
  
  /**
   * Get all accounts
   */
  getAccounts(): Account[] {
    return this.accounts;
  }
  
  /**
   * Get all products
   */
  getProducts(): Product[] {
    return this.products;
  }
  
  /**
   * Get all geographies
   */
  getGeographies(): Geography[] {
    return this.geographies;
  }
  
  /**
   * Get all transactions
   */
  getTransactions(): SalesTransaction[] {
    return this.transactions;
  }
  
  /**
   * Get validation errors
   */
  getValidationErrors(): ValidationError[] {
    return this.validationErrors;
  }
  
  /**
   * Apply an allocation rule to the transactions
   */
  applyAllocationRule(
    scenarioName: string, 
    params: any
  ): RevenueDataModel {
    const rule = allocationRules.find(r => r.scenario === scenarioName);
    
    if (!rule) {
      console.error(`Allocation rule for scenario "${scenarioName}" not found`);
      return this;
    }
    
    this.transactions = rule.apply(this.transactions, params);
    return this;
  }
  
  /**
   * Get revenue by attribution dimension
   */
  getRevenueByAttribution(dimension: AttributionDimension): Record<string, number> {
    const result: Record<string, number> = {};
    
    this.transactions.forEach(transaction => {
      const attributions = applyAttributionRules(transaction);
      const key = attributions[dimension];
      
      if (!result[key]) {
        result[key] = 0;
      }
      
      result[key] += transaction.revenue;
    });
    
    return result;
  }
  
  /**
   * Get total revenue
   */
  getTotalRevenue(): number {
    return this.transactions.reduce((sum, t) => sum + t.revenue, 0);
  }
}
